import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";

// `userCard` carries the `fsrsCardId` study filter; the grapheme-card relations
// supply the only field this read uses (`grapheme`). Invalidating the query key on
// any of these tables (derived from the read's `with`) refetches without
// re-running a full join on every write.
const STUDY_GRAPHEME_WITH = {
  userGraphemeMeaningCard: { columns: { grapheme: true } },
  userGraphemePronunciationCard: { columns: { grapheme: true } },
} as const;

const STUDY_GRAPHEMES_CONFIG = {
  columns: {},
  where: {
    OR: [
      { fsrsCardId: { isNotNull: true } } as const,
      { manuallyMasteredAt: { isNotNull: true } } as const,
    ],
  },
  with: STUDY_GRAPHEME_WITH,
};

export function useGetBrowseLevelsStudyGraphemesQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const withTableChangeVersion = useActorTableChangeVersion("userCard", STUDY_GRAPHEMES_CONFIG);

  return () => withTableChangeVersion(["browseLevelsStudyGraphemes", { actorDb }] as const);
}

export const [useBrowseLevelsStudyGraphemesQueryOptions, browseLevelsStudyGraphemesQueryFn] =
  createUseQueryOptions(
    useGetBrowseLevelsStudyGraphemesQueryKey,
    async ({ queryKey: [_, { actorDb }] }) => {
      // Completion needs only canonical graphemes. Read the projection directly so the
      // cache owns one compact list, not a second copy of every studied card and mnemonic.
      const studyCards = await actorDb(import.meta.location).query.userCard.findMany(
        STUDY_GRAPHEMES_CONFIG,
      );
      const graphemeList: string[] = [];
      const seen = new Set<string>();
      for (const studyCard of studyCards) {
        const grapheme =
          studyCard.userGraphemeMeaningCard?.grapheme ??
          studyCard.userGraphemePronunciationCard?.grapheme;
        if (grapheme === undefined || seen.has(grapheme)) {
          continue;
        }
        seen.add(grapheme);
        graphemeList.push(grapheme);
      }
      return graphemeList;
    },
  );

/**
 * The studied-grapheme set backing the Browse Levels completion state — read by the levels list
 * (current level, completion filters, the page's `ScreenReadyMarker`), the toolbar actions and the
 * header-expansion progress summary. Each of them subscribes here directly.
 *
 * Deliberately progressive (a plain `useQuery`, never suspending): the Browse tab commits
 * immediately and renders the static level catalog while this settles, so each leaf renders its own
 * pending state from the result. `select` is module-level, so the `Set` is rebuilt only when the
 * list itself changes and every subscriber shares it.
 *
 * The options live in the navigator package (not the layout `_context`) so the Browse bottom
 * tab's `prefetch` can warm the query at tab-press time. When it only started once the layout
 * context mounted, the fetch began hundreds of ms after the tap and then waited behind the pager
 * mount + list render churn — putting its entire wall time on the Browse tti path even though the
 * read itself is sub-millisecond.
 */
export function useBrowseLevelsStudyGraphemesQuery() {
  return useQuery({ ...useBrowseLevelsStudyGraphemesQueryOptions(), select: toStudyGraphemeSet });
}

/**
 * The query already projects each studied card's canonical `grapheme`, so the set is built directly
 * rather than through one extra per-grapheme query (an N-query fan-out where one canceled child
 * query kept the whole Levels catalog behind its loading gate after sign-in).
 */
function toStudyGraphemeSet(graphemeList: readonly string[]): ReadonlySet<string> {
  return new Set(graphemeList);
}
